import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { useParams, NavLink, useNavigate, Outlet, useOutletContext } from 'react-router-dom'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import MuiAvatar from '@mui/material/Avatar'
import Skeleton from '@mui/material/Skeleton'
import Fab from '@mui/material/Fab'
import EditIcon from '@mui/icons-material/Edit'
import GroupIcon from '@mui/icons-material/Group'

import socket from '../../app/socket'
import Hero from '../../common/Hero'
import TopBar from '../../common/TopBar'
import Avatar from '../../common/Avatar'
import NavLinkMui from '../../common/NavLinkMui'
import { getMenuTimestamp } from '../../common/time'
import useSocketConnect from '../../hooks/useSocketConnect'
import {
  useGetRoomQuery,
  useListRoomsQuery,
  useCreateRoomMutation,
  useLeaveRoomMutation,
  useAddMembersToRoomMutation,
  selectAllRooms,
  selectRoomById,
} from '../../app/services/rooms/roomsService'
import {
  useListMessagesQuery,
} from '../../app/services/messages/messagesService'
import {
  selectUser,
  logout,
} from '../auth/authSlice'
import { getName } from './util'

const MAX_MESSAGE_LENGTH = 70

export const RoomListItem = ({ id, name, users }) => {

  const user = useSelector(selectUser)
  const api = useSelector((state) => state.api)
  const {
    last_message,
    isSuccess,
    isError,
    error,
    refetch,
  } = useListMessagesQuery(id, {
    selectFromResult: ({ data }) => {
      const num_messages = data?.ids.length || 0
      if (num_messages === 0) { return { last_message: null } }
      const last_id = data.ids[num_messages - 1]
      const last_message = data.entities[last_id]
      return {
        last_message
      }
    }
  })

  useSocketConnect({
    name: 'RoomListItem.listMessages',
    callback: () => {
      console.log('[RoomListItem.listMessages] refetch')
      refetch()
    }
  })

  //console.log('[RoomListItem]', id, last_message)
  const sender_names_by_id = users.reduce((prev, next) => {
    return {...prev, [next.id]: next.name}
  }, {})
  //console.log('sender_names_by_id', sender_names_by_id)

  const member_names = users
    .filter((u) => {
      return u.id !== user.id
    })
    .map((u) => {
      return u.name || u.email
    })
    const isDM = member_names.length === 1
    const isSelf = member_names.length === 0
  //console.log('member_names =', member_names)
  
  const secondary = (message) => {
    const lang_id = (user.default_lang_id in message.translations)
      ? user.default_lang_id
      : ((user.native_lang_id in message.translations)
        ? user.native_lang_id
        : 'en'
      )
    let message_text = message?.translations[lang_id] || ''
    if (message_text.length > MAX_MESSAGE_LENGTH) {
      message_text = `${message_text.slice(0, MAX_MESSAGE_LENGTH-2)}…`
    }
    return (
      <React.Fragment>
        <Typography
          sx={{ display: 'inline', fontWeight: 600 }}
          component="span"
          variant="body2"
          color="palette.grey.800"
        >
          { message.sender_id === user.id
            ? 'You' 
            : sender_names_by_id[message.sender_id] 
          }
        </Typography>
        { ` — ${message_text}` }
      </React.Fragment>
    )
  }

  const no_messages_yet = (
    <React.Fragment>
      No messages yet
    </React.Fragment>
  )

  const primary_name = name
    ? name
    : (isDM
      ? member_names[0]
      : 'Notes To Self'
    )

  const timestamp = Boolean(last_message)
    ? getMenuTimestamp(last_message.created_at)
    : ''

  // There seems to be a bug in RTKquery making isLoading not coming correctly
  // This fixes it
  const isLoading = `listMessages("${id}")` in api.queries
    ? (last_message === null && api.queries[`listMessages("${id}")`].status === "pending")
    : false

  return (
    <ListItem
      component={NavLinkMui}
      to={`/rooms/${id}`}
      alignItems='flex-start'
      sx={{
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <ListItemAvatar>
        <Avatar
          icon={
            (!isDM && !isSelf)
            ? <GroupIcon />
            : null
          }
          name={ name }
        />
      </ListItemAvatar>
      <ListItemText
        primary={ name }
        primaryTypographyProps={{ sx: { fontWeight: 500, color: 'palette.white' } }}
        secondary={
          isLoading
          ? <Skeleton animation='wave' />
          : ( Boolean(last_message) 
            ? secondary(last_message)
            : no_messages_yet
          )}
      />
      {Boolean(last_message) && (
        <Typography
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            textAlign: 'right',
            fontSize: '0.8rem',
            color: 'grey.300',
          }}
        >
          {timestamp}
        </Typography>
      )}
    </ListItem>
  )
}

const EmptyRoomList = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mt: 20,
      }}
    >
      <Hero />
      <Typography component="h6" variant="h6">
        No channels yet
      </Typography>
      <Link
        component={NavLink}
        to={'/rooms/new'}
        variant="body1"
        sx={{
          mt: 2,
          mb: 2
        }}
      >
          {`Create a channel now`}
      </Link>
    </Box>
  )
}

const list_skeleton = (
  <List>
  {[...Array(10).keys()].map((i) => {
    return (
      <ListItem key={i}>
        <ListItemAvatar>
          <Skeleton variant='circular' animation='wave'> 
            <Avatar icon={<GroupIcon />} name='Some name' />
          </Skeleton>
        </ListItemAvatar>
        <Stack width="100%" spacing={1}>
          <Skeleton width="100%" variant="rounded" animation='wave'>
            <Typography variant='h7'>.</Typography>
          </Skeleton>
          <Skeleton width="100%" variant="rounded" animation='wave'>
            <Typography variant='h8'>.</Typography>
          </Skeleton>
        </Stack>
      </ListItem>
    )
  })}
  </List>
)

export const RoomsList = () => {
  //console.log('[Rooms]')
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector(selectUser)
  const rooms = useSelector(selectAllRooms)
  const [isLoading, refetch] = useOutletContext()
  const [anchorEl, setAnchorEl] = React.useState(null)

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  }

  const handleClose = () => {
    setAnchorEl(null);
  }

  const menu_items = [
    {
      name: 'Create channel',
      onClick: () => navigate('/rooms/new')
    },
    {
      name: 'Reload channels',
      onClick: refetch
    },
    {
      name: 'Edit Profile',
      onClick: () => navigate('/settings')
    },
    {
      name: 'Read the Blog',
      onClick: () => {
        window.open(
          'https://babelchat.wordpress.com',
          '_blank').focus()
      }
    },
    {
      name: 'Get help',
      onClick: () => navigate('/help')
    },
    {
      name: 'Give feedback',
      onClick: () => navigate('/feedback')
    },
    {
      name: 'Terms of Use',
      onClick: () => {
        window.open(
          process.env.REACT_APP_API_BASE_URL + '/Babel_Chat_Terms_Mar2024.pdf',
          '_blank').focus()
      }
    },
    {
      name: 'Privacy Policy',
      onClick: () => {
        window.open(
          process.env.REACT_APP_API_BASE_URL + '/Babel_Chat_Privacy_Policy_Mar2024.pdf',
          '_blank').focus()
      }
    },
    {
      name: 'Log out',
      onClick: () => dispatch(logout())
    }
  ]

  //console.log('rooms =', rooms)
  return (
    <Box
      sx={{
        flexGrow: 1,
      }}
    >
      <TopBar
        left={
          <IconButton
            sx={{
              width: '40px',
              marginRight: '16px',
            }}
            size="medium"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={() => navigate('/settings')}
            color="inherit"
          >
            {user
              ? <Avatar name={user.name} />
              : (
                <Skeleton variant='circular' animation='wave' height='25px' width='40px' />
              )
            }
          </IconButton>
        }
        title='Babel Chat'
        menuItems={menu_items}
      />
      {(isLoading || !user)
        ? list_skeleton
        : (
          rooms.length === 0
          ? <EmptyRoomList />
          : (
            <React.Fragment>
              <List sx={{
                width: '100%',
              }}
              >
                {rooms !== undefined && (
                  rooms.map((room, j) => {
                    return <RoomListItem key={room.id}
                      {...room}
                      name={ getName(room, user) } 
                    />
                  })
                )}
              </List>
              <Box sx={{ height: '80px' }} />
            </React.Fragment>
          )
        )
      }
      <Fab
        color="primary"
        size="medium"
        aria-label="New channel"
        onClick={() => navigate('/rooms/new')}
        sx={{
          position: 'fixed',
          right: '20px',
          bottom: '20px'
        }}
      >
        <EditIcon />
      </Fab>
    </Box>
  )
}

export const RoomsWrapper = () => {

  const user = useSelector(selectUser)
  const {
    data,
    isLoading,
    isSuccess,
    isError,
    error,
    refetch,
  } = useListRoomsQuery()

  useSocketConnect({
    name: 'RoomsWrapper.listRooms',
    callback: () => {
      console.log('[RoomsWrapper.listRooms] refetch')
      refetch()
    }
  })

  //console.log('[RoomsWrapper] user =', user)
  return (
    <Outlet context={[isLoading, refetch]} />
  )
}
